/**
 * ZALOZENI ZAKAZKY — JEDNO MISTO (krok PIL1, pravidlo O3 z generalky GEN1).
 *
 * Blokator O3: zakazka zalozena pres `POST /v1/cases` BEZ `propertyId` nejde
 * pouzit — nabor na miste se do ni neulozi („Údaje se ukládají do zakázky.
 * Vyberte nejdřív zakázku.", 422) a `cases.property_id` uz dodatecne
 * nedostane, protoze se nastavuje jen pri zalozeni. GEN1 to opravil v
 * pruvodci „Nová"; PIL1 pridava druhou cestu („Převzít do zakázky" z tipu
 * akvizice), a dve kopie teze posloupnosti by se rozesly (CLAUDE.md §8.2).
 *
 * Server nemovitost se stejnou adresou DOHLEDA (dedupe), takze druha zakazka
 * nad tymz bytem novou nemovitost nezaklada.
 */

import { idempotencyKey, type ApiPaths } from '@reos/api-client';
import { apiClient } from './api.js';
import { DomainApiError, unwrap } from './api-error.js';
import { propertyAddressOf } from './address-query.js';

/** Krok PIL1: vyklad adresy z jednoho radku je sdileny (viz `address-query.ts`). */
export { propertyAddressOf };

/**
 * Druh nemovitosti z OpenAPI kontraktu — zadny opsany vycet. Krok UXZ1 pribral
 * `complex` (volba „Ostatní" v zalozeni zakazky), takze opsana trojice uz
 * kontraktu neodpovidala.
 */
export type NewPropertyKind = NonNullable<
  ApiPaths['/v1/properties']['post']['requestBody']['content']['application/json']['kind']
>;

/** Druh zakazky z OpenAPI kontraktu — zadny opsany vycet. */
export type NewCaseType =
  ApiPaths['/v1/cases']['post']['requestBody']['content']['application/json']['type'];

type NewPropertyBody = ApiPaths['/v1/properties']['post']['requestBody']['content']['application/json'];

/** Jednotka a parcela z kontraktu nemovitosti (krok UXP1 — identita se UKLADA). */
export type NewPropertyUnit = NonNullable<NewPropertyBody['units']>[number];
export type NewPropertyParcel = NonNullable<NewPropertyBody['parcels']>[number];

/**
 * Role strany zakazky. Tentyz slovnik cte karta „Lidé" v detailu zakazky
 * (`components/case-aside.tsx` `ROLE_CAPTION`) i sloupec „Role" v adresari
 * klientu — proto tu nesmi vzniknout druhy vycet.
 */
export type CaseClientRole = 'seller' | 'buyer' | 'landlord' | 'tenant';

/**
 * Klient z formulare „Založit zakázku" (krok UXP1, nalez P1-1).
 *
 * Do UXP1 slo jmeno klienta do API jen jako TEXT v popisu zakazky, takze
 * strana zakazky nevznikla a klient nebyl v adresari — faktura „Ze zakázky"
 * pak hlasila „Zakázka nemá žádné strany" a smlouva se nepredvyplnila.
 */
export interface CaseClientInput {
  readonly kind: NonNullable<ApiPaths['/v1/parties']['post']['requestBody']['content']['application/json']['kind']>;
  readonly displayName: string;
  /** E-mail a telefon z formulare; kratsi nez 3 znaky kontrakt nepusti. */
  readonly contacts: readonly { readonly kind: 'email' | 'phone'; readonly value: string }[];
  readonly role: CaseClientRole;
}

export interface CaseOverPropertyInput {
  /** Nazev zakazky (predvypleny adresou, stejne jako v pruvodci „Nová"). */
  readonly title: string;
  /**
   * Adresa, podle ktere server nemovitost dohleda nebo zalozi. Krok UXP1
   * (nalez P1-2): u pozemku zadaneho jen parcelou je PRAZDNA — nemovitost pak
   * nese jen nazev a parcelu, protoze adresa neni znama a vymyslet ji nesmime.
   */
  readonly addressQuery: string;
  readonly propertyKind: NewPropertyKind;
  readonly caseType: NewCaseType;
  /** Bod z napovedy adres nebo z mapy; bez nej se nic nedomysli. */
  readonly location?: { readonly lat: number; readonly lon: number } | null | undefined;
  /**
   * Krok UXZ1: poznamka z prvniho hovoru (a cenova predstava). Jde do
   * `cases.description` — zakazka pri zalozeni jine pole na volny text nema a
   * seznam zakazek popis uz kresli pod adresou (`lib/case-rows.ts`).
   */
  readonly description?: string | undefined;
  /**
   * Krok UXP1 (nalez P1-1): klient z formulare. Zalozi se jako strana
   * (`POST /v1/parties`) a rovnou se pripoji k zakazce jako ucastnik, takze je
   * videt v karte „Lidé", v adresari Klienti i ve fakture „Ze zakázky".
   */
  readonly client?: CaseClientInput | null | undefined;
  /**
   * Krok UXP5 bod 2 — CENOVA PREDSTAVA JAKO UDAJ NEMOVITOSTI.
   *
   * Do UXP5 slo cislo z pole „Cenová představa" JEN do `cases.description`
   * jako veta. Inzerat (`asking_price` v konceptu) ani faktura provize
   * (zaklad) z toho necetly nic — makler musel cenu psat znovu a pri
   * nataceni 11. 10. to skoncilo prazdnym polem ceny v inzeratu.
   * Zapisuje se jako TVRZENI MAJITELE (`OWNER_CLAIM`/`owner`), protoze to je
   * presne to, co to je — nikoli urední cena.
   *
   * Krok PROP3 bod 3: uklada se jako PREDSTAVA MAJITELE
   * (`sale.owner_price_expectation`). Nabidkovou cenou (`asking_price`, ze
   * ktere zije inzerat a verejna stranka) se stane JEN tehdy, kdyz to makler
   * vyslovne rekne (`priceIsAsking`) — cislo z prvniho hovoru neni cena,
   * pod kterou kancelar nemovitost nabizi (audit propojeni 14. 10., nalez 12).
   */
  readonly askingPrice?: { readonly amount: string; readonly currency: string } | null | undefined;
  /** `true` = makler zaskrtl „Toto je nabídková cena" (jinak jen predstava majitele). */
  readonly priceIsAsking?: boolean | undefined;
  /** Cislo jednotky z formulare — identita nemovitosti, ne text v popisu. */
  readonly units?: readonly NewPropertyUnit[] | undefined;
  /** Parcela z formulare (pozemek bez adresy, nalez P1-2). */
  readonly parcels?: readonly NewPropertyParcel[] | undefined;
  /**
   * Krok P8F1 bod 2 — IDEMPOTENCNI KLIC JEDNOHO ODESLANI PRUVODCE.
   *
   * Dosud se generoval UVNITR teto funkce (`crypto.randomUUID()`), takze
   * dvojklik i opakovani po sitove chybe posilalo KAZDE sve volani s novym
   * klicem a zalozilo druhou zakazku. Klic drzi obrazovka pro celou dobu
   * jednoho zalozeni vcetne opakovani (viz `zakazka-novy.tsx`).
   */
  readonly submissionKey: string;
  /** Odpoved maklere na dotaz „Jde o tentyz byt?" (krok P8F1 bod 1b). */
  readonly samePropertyDecision?:
    | { readonly propertyId: string; readonly same: boolean }
    | undefined;
  /**
   * Krok P8F6 bod 1: udaje z pruvodce, ktere patri na KARTU nemovitosti
   * (dispozice, cislo jednotky, katastralni uzemi). Cena se sem nepredava —
   * tu zna `askingPrice` a klic k ni urcuje `priceIsAsking` (PROP3 bod 3).
   */
  readonly facts?: readonly CaseFactInput[] | undefined;
  /**
   * Krok P8F6 bod 2: klient, ktereho makler vybral z adresare („Použít
   * existujícího klienta?"). Kdyz je vyplneny, NOVA strana se nezaklada —
   * zakazka dostane ucastnika s timto `partyId` a v adresari nevznikne druhy
   * Jan Novak s tymz e-mailem. Slucovani existujicich stran se tim nedeje
   * (to ma vlastni cestu `merge-suggestions`).
   */
  readonly clientPartyId?: string | undefined;
}

/** Vysledek zalozeni — id zakazky a udaje, ktere se na kartu nepodarilo ulozit. */
export interface CaseCreateResult {
  readonly caseId: string;
  readonly propertyId: string;
  /** Klice faktu, ktere server odmitl (krok P8F6 bod 1); prazdne = vse ulozeno. */
  readonly factsFailed: readonly string[];
}

/** Kontrakt pusti kontakt od 3 znaku — kratsi vstup by zalozeni shodil na 400. */
const MIN_CONTACT_LENGTH = 3;

/**
 * Krok P8F1 bod 2 — IDEMPOTENCNI KLIC JEDNOHO VOLANI.
 *
 * Klic jednoho odeslani (`submissionKey`) se misi s OTISKEM TELA, protoze
 * `withIdempotency` vraci na tyz klic s jinym telem 409: kdyz makler po
 * chybe opravi telefon a odesle znovu, strana uz je zalozena pod starym
 * telem a 409 by mu zalozeni zablokoval. Stejne telo -> stejny klic ->
 * prehrani bez duplikatu; zmenene telo -> jiny klic -> novy pokus.
 *
 * FNV-1a (32 bit) staci: otisk neni bezpecnostni prvek, jen oddeluje pokusy
 * jedne obrazovky. Charset `[A-Za-z0-9._:-]` je dany kontraktem hlavicky.
 */
function callKey(submissionKey: string, scope: string, body: unknown): string {
  let hash = 0x811c9dc5;
  const text = JSON.stringify(body) ?? '';
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return `${submissionKey}-${scope}-${hash.toString(36)}`;
}

/** Typ problemu, kterym `POST /v1/properties` hlasi uz pouzitou parcelu (krok PRF3 bod 1). */
const PARCELS_ALREADY_USED_TYPE = 'property/parcels-already-used';

/**
 * ID nemovitosti, ktere uz zadanou parcelu maji — nebo `null`, kdyz chyba je
 * o necem jinem.
 *
 * Server je posila v `detail` jako `propertyIds=<id>,<id>` (problem+json nema
 * misto na vlastni polozky, viz `ProblemError`), takze rozbor patri na JEDNO
 * misto vedle kontraktu zalozeni — ne do kazde obrazovky, ktera nemovitost
 * zaklada.
 */
export function parcelsAlreadyUsedPropertyIds(error: unknown): readonly string[] | null {
  if (!(error instanceof DomainApiError)) return null;
  if (error.type === undefined || !error.type.endsWith(PARCELS_ALREADY_USED_TYPE)) return null;
  const match = /propertyIds=([^\s]*)/u.exec(error.message);
  const ids = (match?.[1] ?? '').split(',').filter((id) => id !== '');
  return ids;
}

/** Typ problemu, kterym server zada potvrzeni „Jde o tentyz byt?" (P8F1 bod 1b). */
const SAME_PROPERTY_CONFIRMATION_TYPE = 'property/same-property-confirmation-required';

/** Karta, kterou server nasel na teze parcele a ktera jde makleri k potvrzeni. */
export interface SamePropertyCandidate {
  readonly propertyId: string;
  readonly address: string | null;
  readonly units: readonly string[];
  readonly disposition: string | null;
}

/**
 * Nalezena karta z 409 `property/same-property-confirmation-required`, nebo
 * `null`, kdyz chyba je o necem jinem.
 *
 * Server ji posila v `detail` jako JSON (problem+json nema misto na vlastni
 * polozky), takze rozbor patri na JEDNO misto vedle kontraktu zalozeni —
 * presne tam, kde uz zije `parcelsAlreadyUsedPropertyIds`.
 */
export function samePropertyCandidate(error: unknown): SamePropertyCandidate | null {
  if (!(error instanceof DomainApiError)) return null;
  if (error.type === undefined || !error.type.endsWith(SAME_PROPERTY_CONFIRMATION_TYPE)) return null;
  try {
    const parsed = JSON.parse(error.message) as Partial<SamePropertyCandidate>;
    if (typeof parsed.propertyId !== 'string' || parsed.propertyId === '') return null;
    return {
      propertyId: parsed.propertyId,
      address: typeof parsed.address === 'string' ? parsed.address : null,
      units: Array.isArray(parsed.units) ? parsed.units.filter((u): u is string => typeof u === 'string') : [],
      disposition: typeof parsed.disposition === 'string' ? parsed.disposition : null,
    };
  } catch {
    // Server poslal neco jineho nez JSON — pro makleere je to obecna chyba.
    return null;
  }
}

/**
 * Zalozi klienta jako stranu. Vraci `null`, kdyz makler jmeno nevyplnil —
 * zakazka pak vznikne bez strany presne jako dosud.
 */
async function createCaseClient(
  client: CaseClientInput | null | undefined,
  submissionKey: string,
  existingPartyId?: string | undefined,
): Promise<string | null> {
  // Krok P8F6 bod 2: makler vybral klienta z adresare — druhy se nezaklada.
  if (existingPartyId !== undefined && existingPartyId !== '') return existingPartyId;
  if (client == null || client.displayName.trim() === '') return null;
  const contacts = client.contacts
    .map((contact) => ({ kind: contact.kind, value: contact.value.trim() }))
    .filter((contact) => contact.value.length >= MIN_CONTACT_LENGTH);
  const body = {
    kind: client.kind,
    displayName: client.displayName.trim().slice(0, 200),
    country: 'CZ' as const,
    ...(contacts.length > 0 ? { contacts } : {}),
  };
  const party = await unwrap(
    apiClient.POST('/v1/parties', {
      // Krok P8F1 bod 2: klic jednoho odeslani, ne nahoda per volani.
      params: { header: idempotencyKey(callKey(submissionKey, 'party', body)) },
      body,
    }),
  );
  return party.party.id;
}

/**
 * Fakta, ktera ma pruvodce zapsat na kartu nemovitosti.
 *
 * Krok P8F6 bod 1: cena uz tudy sla (UXP5/PROP3), nove jde TOUTEZ cestou
 * dispozice, cislo jednotky a katastralni uzemi — co makler napsal do
 * pruvodce, uz se ho nabor nema ptat podruhe. Klice jsou z registru
 * `FACT_KEYS`, puvod resi `provenance` volajiciho.
 */
export interface CaseFactInput {
  readonly factKey: string;
  readonly value: unknown;
  readonly provenance: { readonly status: 'OWNER_CLAIM' | 'UNKNOWN'; readonly sourceType: 'owner' | 'user' };
}

/** Cena z formulare jako fakt (krok UXP5 bod 2, klic podle PROP3 bodu 3). */
function priceFact(
  price: CaseOverPropertyInput['askingPrice'],
  isAsking: boolean,
): CaseFactInput | null {
  if (price == null) return null;
  return {
    /*
     * Krok PROP3 bod 3 — KTERY KLIC: vychozi je `sale.owner_price_expectation`
     * („představa majitele", jen informace). `asking_price` je NABIDKOVA cena,
     * ze ktere zije inzerat, verejna stranka a zaklad provize — tam cislo jde
     * jen na vyslovny pokyn maklere. Zapisuje se vzdy jen JEDEN klic.
     */
    factKey: isAsking ? 'asking_price' : 'sale.owner_price_expectation',
    value: { amount: price.amount, currency: price.currency },
    // Cislo z prvniho hovoru je TVRZENI MAJITELE, ne udaj maklere.
    provenance: { status: 'OWNER_CLAIM', sourceType: 'owner' },
  };
}

/**
 * Zapise fakta na kartu nemovitosti. Vraci klice, ktere se NEPODARILO
 * zapsat — obrazovka o nich rekne makleri (krok P8F6 bod 1).
 *
 * Do P8F6 byl zapis ceny „zamerne tichy" (`catch {}`) a cena se tim pri 403
 * ztratila BEZ STOPY (meereno zive 27. 10.: pruvodce dostal na novou kartu
 * bytu 403, protoze fakta zapisoval JESTE PRED zalozenim zakazky — a dokud
 * zakazka nestoji, makler ke karte zadny vztah nema, takze `property` s
 * dosahem `own` zapis zamitne). Zakazka uz je v tu chvili zalozena, takze
 * selhani nesmi vyhodit vyjimku; jde na obrazovku jako vyzva „Zkusit znovu".
 */
async function writeFacts(
  propertyId: string,
  facts: readonly CaseFactInput[],
): Promise<readonly string[]> {
  const failed: string[] = [];
  for (const fact of facts) {
    try {
      await unwrap(
        apiClient.PUT('/v1/properties/{id}/facts', {
          params: { path: { id: propertyId } },
          body: { factKey: fact.factKey, value: fact.value, provenance: fact.provenance },
        }),
      );
    } catch {
      failed.push(fact.factKey);
    }
  }
  return failed;
}

/** Zalozi nemovitost (nebo ji dohleda) a nad ni zakazku. Vraci id zakazky. */
export async function createCaseOverProperty(input: CaseOverPropertyInput): Promise<CaseCreateResult> {
  const partyId = await createCaseClient(input.client, input.submissionKey, input.clientPartyId);
  const propertyBody = {
    kind: input.propertyKind,
    country: 'CZ' as const,
    name: input.addressQuery === '' ? input.title : input.addressQuery,
    // Prazdna adresa se NEPOSILA: `propertyAddressOf('')` by zalozila
    // nemovitost s prazdnou ulici a dedupe `normalized_key` by slil vsechny
    // pozemky bez adresy do jedne nemovitosti (nalez P1-2).
    ...(input.addressQuery === ''
      ? {}
      : { address: propertyAddressOf(input.addressQuery, input.location) }),
    ...(input.units !== undefined && input.units.length > 0 ? { units: [...input.units] } : {}),
    ...(input.parcels !== undefined && input.parcels.length > 0 ? { parcels: [...input.parcels] } : {}),
    ...(input.samePropertyDecision !== undefined
      ? { samePropertyDecision: input.samePropertyDecision }
      : {}),
  };
  const property = await unwrap(
    apiClient.POST('/v1/properties', {
      params: { header: idempotencyKey(callKey(input.submissionKey, 'property', propertyBody)) },
      body: propertyBody,
    }),
  );
  const caseBody = {
    type: input.caseType,
    title: input.title,
    propertyId: property.id,
    ...(input.description !== undefined && input.description !== '' ? { description: input.description } : {}),
    ...(partyId !== null && input.client != null
      ? { participants: [{ partyId, role: input.client.role }] }
      : {}),
  };
  const created = await unwrap(
    apiClient.POST('/v1/cases', {
      params: { header: idempotencyKey(callKey(input.submissionKey, 'case', caseBody)) },
      body: caseBody,
    }),
  );
  /*
   * Krok P8F6 bod 0 — FAKTA SE ZAPISUJI TEPRVE NAD HOTOVOU ZAKAZKOU.
   *
   * Dosud sel zapis ceny hned za zalozenim karty, tedy v okamziku, kdy ke
   * karte NEEXISTOVALA zadna zakazka makleere: `getPropertyAbacFields` cte
   * vlastnictvi karty z AKTIVNICH ZAKAZEK nad ni (`cases.owner_user_id`),
   * takze `property.property.update` s dosahem `own` zapis zamitl (403) a
   * cena se tise ztratila. Po zalozeni zakazky vztah existuje a zapis projde
   * — zadne rozsirovani `ROLE_REACH` ani vyjimka v policy (vzor P8F3).
   */
  const price = priceFact(input.askingPrice, input.priceIsAsking === true);
  const facts = [...(price === null ? [] : [price]), ...(input.facts ?? [])];
  const factsFailed = facts.length === 0 ? [] : await writeFacts(property.id, facts);
  return { caseId: created.id, propertyId: property.id, factsFailed };
}
